import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import Logo from '../common/Logo';
import { FiSettings, FiHexagon, FiAward } from 'react-icons/fi';
import { userData, badges } from '../data/mockData';

export default function Profile() {
  return (
    <div className="max-w-5xl mx-auto space-y-12">
      {/* Profile Header */}
      <div className="bg-navy-900 rounded-[2.5rem] border border-navy-800 p-8 md:p-12 relative overflow-hidden shadow-2xl">
        {/* High-Quality Visual Background */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <img 
            src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1200&q=80" 
            alt="Network Background" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-br from-quest-500/10 via-neon-blue/5 to-transparent rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
        
        <div className="relative z-10 flex flex-col md:flex-row items-center md:items-start gap-10">
          <div className="relative">
            <div className="w-40 h-40 rounded-[2rem] overflow-hidden border-4 border-navy-800 relative z-10 shadow-2xl">
              <img src={userData.avatar} alt="Profile" className="w-full h-full object-cover" />
            </div>
            {/* Level Badge Overlay */}
            <div className="absolute -bottom-4 -right-4 bg-navy-950 border-2 border-quest-500 text-quest-500 font-black font-mono w-14 h-14 flex items-center justify-center rounded-2xl z-20 shadow-glow-green transform rotate-12">
              {userData.level}
            </div>
          </div>

          <div className="flex-1 text-center md:text-left pt-2">
            <div className="flex flex-col md:flex-row items-center gap-4 mb-6">
              <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-white uppercase">{userData.name}</h1>
              <span className="px-4 py-1.5 rounded-full bg-navy-800 text-quest-400 text-[10px] font-mono border border-quest-500/30 uppercase tracking-[0.2em] shadow-glow-green/20">
                {userData.rank}
              </span>
            </div>

            <div className="max-w-md mx-auto md:mx-0 space-y-4">
              <div className="flex justify-between items-end text-[10px] font-mono uppercase tracking-widest">
                <span className="text-gray-500">Neural Sync Status</span>
                <span className="text-quest-500 font-black">{Math.round((userData.xp / userData.nextLevelXp) * 100)}%</span>
              </div>
              <div className="h-4 w-full bg-navy-950 rounded-full overflow-hidden border border-navy-800 p-1 shadow-inner">
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: `${(userData.xp / userData.nextLevelXp) * 100}%` }}
                  transition={{ duration: 2, ease: "easeOut" }}
                  className="h-full bg-gradient-to-r from-quest-600 via-quest-500 to-neon-blue rounded-full shadow-glow-green" 
                />
              </div>
              <div className="text-[10px] text-gray-600 font-mono text-center md:text-left">
                {userData.xp} / {userData.nextLevelXp} XP UNTIL NEXT ASCENSION
              </div>
            </div>
          </div>

          <button className="p-4 bg-navy-800 hover:bg-navy-700 rounded-2xl border border-navy-700 transition-all hover:scale-110 shadow-xl group">
            <SafeIcon icon={FiSettings} className="text-2xl text-gray-400 group-hover:text-white group-hover:rotate-90 transition-all duration-500" />
          </button>
        </div>
      </div>

      {/* Badge Shelf */}
      <section>
        <div className="flex items-center justify-between mb-8 border-b border-navy-800 pb-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-trophy-500/10 rounded-xl border border-trophy-500/20">
              <SafeIcon icon={FiHexagon} className="text-2xl text-trophy-500" />
            </div>
            <h2 className="text-2xl font-black uppercase tracking-tight text-white">Achievement Vault</h2>
          </div>
          <div className="text-[10px] font-mono text-gray-500 uppercase tracking-widest">
            {badges.filter(b => b.earned).length} / {badges.length} Unlocked
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {badges.map((badge, idx) => (
            <motion.div
              key={badge.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className={`group p-6 rounded-3xl border flex flex-col items-center text-center transition-all ${
                badge.earned 
                  ? 'bg-navy-900 border-navy-700 hover:border-trophy-500/50 hover:shadow-glow-gold hover:-translate-y-2' 
                  : 'bg-navy-950 border-navy-800 opacity-40 grayscale blur-[1px]'
              }`}
            >
              <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 transition-transform group-hover:scale-110 ${
                badge.earned ? 'bg-trophy-500/10 text-trophy-500 shadow-glow-gold/10' : 'bg-navy-800 text-gray-700'
              }`}>
                <SafeIcon icon={badge.icon} className="text-4xl" />
              </div>
              <h3 className={`font-black text-sm mb-2 uppercase tracking-tight ${badge.earned ? 'text-white' : 'text-gray-600'}`}>
                {badge.title}
              </h3>
              <p className="text-[10px] text-gray-500 leading-relaxed font-medium">
                {badge.desc}
              </p>
              {badge.earned && (
                <div className="mt-4 pt-4 border-t border-navy-800 w-full text-[8px] font-mono text-gray-600 uppercase tracking-[0.1em]">
                  Earned {badge.date}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* Footer Branding */}
      <footer className="pt-20 pb-10 text-center">
        <div className="inline-flex flex-col items-center gap-4 group">
          <Logo className="w-12 h-12 text-gray-800 group-hover:text-quest-500 transition-colors duration-700" color="currentColor" />
          <div className="h-px w-20 bg-gradient-to-r from-transparent via-navy-800 to-transparent" />
          <p className="text-[10px] text-navy-600 font-mono uppercase tracking-[0.3em] font-black">
            Made by <span className="text-gray-500">LingLink AI</span> and <span className="text-gray-500">DigLab AI</span>
          </p>
        </div>
      </footer>
    </div>
  );
}